import {
  apiCodeQuery,
  pagedQuery,
  toPaged,
  type IPagedCollection,
  type PagedQueryParams,
} from '@moko/utils';
import { internalFetch } from '../utils/fetchHelper';
import { fromApiDateOnly, toApiDateOnly } from '../utils/dateOnly';

export const isValidDayOfWeek = (value: unknown): value is number =>
  typeof value === 'number' &&
  Number.isInteger(value) &&
  value >= 1 &&
  value <= 7;

export const isValidDaysOfWeek = (value: unknown): value is number[] =>
  Array.isArray(value) && value.every(isValidDayOfWeek);

export const DAY_OF_WEEK = [
  { value: 1, key: 'monday', fallback: 'Monday' },
  { value: 2, key: 'tuesday', fallback: 'Tuesday' },
  { value: 3, key: 'wednesday', fallback: 'Wednesday' },
  { value: 4, key: 'thursday', fallback: 'Thursday' },
  { value: 5, key: 'friday', fallback: 'Friday' },
  { value: 6, key: 'saturday', fallback: 'Saturday' },
  { value: 7, key: 'sunday', fallback: 'Sunday' },
] as const;

type ContractorAvailabilityRuleFields = {
  startTime: string;
  endTime: string;
  timezoneId: string;
  effectiveFrom: string;
  effectiveTo: string;
  status: boolean;
};

export interface IContractorAvailabilityRule
  extends ContractorAvailabilityRuleFields {
  id: number;
  contractorId: number;
  dayOfWeek: number;
}

export interface ICreateContractorAvailabilityRuleRequest
  extends ContractorAvailabilityRuleFields {
  contractorId: number;
  daysOfWeek: number[];
}

export interface IUpdateContractorAvailabilityRuleRequest
  extends ContractorAvailabilityRuleFields {
  contractorId: number;
  dayOfWeek: number;
}

export type ContractorAvailabilityDraft = Omit<
  ICreateContractorAvailabilityRuleRequest,
  'contractorId'
> & { id: number };

export const normalizeContractorAvailabilityRuleDates = <
  T extends { effectiveFrom: string; effectiveTo: string }
>(
  rule: T
): T => ({
  ...rule,
  effectiveFrom: fromApiDateOnly(rule.effectiveFrom),
  effectiveTo: fromApiDateOnly(rule.effectiveTo),
});

export type GetContractorAvailabilityRulesParams = PagedQueryParams;

const apiUrl = (): string => import.meta.env.VITE_BACKEND_API_URL as string;
const apiKey = (): string => import.meta.env.VITE_BACKEND_API_KEY as string;

export const createContractorAvailabilityRule = async (
  abortController: AbortController,
  data: ICreateContractorAvailabilityRuleRequest
): Promise<void> => {
  const payload = {
    ...data,
    effectiveFrom: toApiDateOnly(data.effectiveFrom),
    effectiveTo: toApiDateOnly(data.effectiveTo),
  };

  await internalFetch(
    `${apiUrl()}/p/contractorAvailabilityRule`,
    'POST',
    abortController,
    JSON.stringify(payload),
    true
  );
};

export const updateContractorAvailabilityRule = async (
  abortController: AbortController,
  id: number,
  data: IUpdateContractorAvailabilityRuleRequest
): Promise<void> => {
  const payload = {
    ...data,
    effectiveFrom: toApiDateOnly(data.effectiveFrom),
    effectiveTo: toApiDateOnly(data.effectiveTo),
  };

  await internalFetch(
    `${apiUrl()}/p/contractorAvailabilityRule?id=${id}`,
    'PUT',
    abortController,
    JSON.stringify(payload),
    true
  );
};

export const deleteContractorAvailabilityRule = async (
  abortController: AbortController,
  id: number
): Promise<void> => {
  await internalFetch(
    `${apiUrl()}/p/contractorAvailabilityRule?id=${id}`,
    'DELETE',
    abortController,
    undefined,
    true
  );
};

export const getContractorAvailabilityRule = async (
  abortController: AbortController,
  id: number
): Promise<IContractorAvailabilityRule> => {
  const rule = await internalFetch(
    `${apiUrl()}/p/contractorAvailabilityRule?id=${id}${apiCodeQuery(
      apiKey()
    )}`,
    'GET',
    abortController
  );

  return normalizeContractorAvailabilityRuleDates(rule);
};

export const getContractorAvailabilityRules = async (
  abortController: AbortController,
  contractorId: number,
  params: GetContractorAvailabilityRulesParams
): Promise<IPagedCollection<IContractorAvailabilityRule>> => {
  const result = await internalFetch(
    `${apiUrl()}/p/contractor/${contractorId}/contractorAvailabilityRules?${pagedQuery(
      params,
      apiKey()
    )}`,
    'GET',
    abortController
  );
  const paged = toPaged<IContractorAvailabilityRule>(
    result,
    params.pageSize,
    params.pageNo
  );
  return {
    ...paged,
    items: paged.items.map(normalizeContractorAvailabilityRuleDates),
  };
};
